import React, { useState, useEffect } from 'react';
import { Segment, Form, Button, Input, Header, Divider, Dropdown, Message, Icon, Grid, Tab } from 'semantic-ui-react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { post } from '../../../util/api';

const Mail = ({ product }) => {
  const { t } = useTranslation();
  const [mailTemplate, setMailTemplate] = useState('');
  const [preferredMailService, setPreferredMailService] = useState('smtp');
  const [subject, setSubject] = useState('');
  const [smtpSettings, setSmtpSettings] = useState({
    host: '',
    port: '',
    user: '',
    pass: '',
    secure: false,
    from: ''
  });

  const [activeTab, setActiveTab] = useState('smtp');

  const handleTabChange = (e, { activeIndex }) => {
    const newService = activeIndex === 0 ? 'smtp' : 'sendinblue';
    setActiveTab(newService);
    setPreferredMailService(newService);
  };

  const [preview, setPreview] = useState('');
  const [variables] = useState([
    { key: 'name', text: 'name', value: '{{name}}' },
    { key: 'question', text: 'question', value: '{{question}}' },
    { key: 'answer', text: 'answer', value: '{{answer}}' },
    { key: 'productlink', text: 'productlink', value: '{{productlink}}' },
  ]);
  const [selectedVariable, setSelectedVariable] = useState('');
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    if (product && product.settings && product.settings.questionAnswerData) {
      const { mailTemplate, smtpSettings, preferredMailService: savedPreferredMailService } = product.settings.questionAnswerData;
      
      setMailTemplate(mailTemplate || '');
      setSubject(smtpSettings?.subject || '');
      setSmtpSettings({
        host: smtpSettings?.host || '',
        port: smtpSettings?.port || '',
        user: smtpSettings?.user || '',
        pass: smtpSettings?.pass || '',
        secure: smtpSettings?.secure || false,
        from: smtpSettings?.from || '',
        sendinblueApiKey: smtpSettings?.sendinblueApiKey || ''
      });
      setPreferredMailService(savedPreferredMailService || 'smtp');
    }
  }, [product]);

  const handleTemplateChange = (content) => {
    setMailTemplate(content);
  };

  useEffect(() => {
    setActiveTab(preferredMailService);
  }, [preferredMailService]);

  const handleSubjectChange = (e, { value }) => {
    setSubject(value);
  };

  const handleSmtpChange = (e, { name, value }) => {
    setSmtpSettings(prev => ({ ...prev, [name]: value }));
  };

  const handlePreview = () => {
    let previewContent = mailTemplate;
    variables.forEach(variable => {
      previewContent = previewContent.replace(variable.value, `<strong>${variable.text}</strong>`);
    });
    setPreview(previewContent);
  };

  const handleSave = async () => {
    const mailSettings = {
      pid: product.id,
      mailTemplate,
      preferredMailService,
      smtpSettings: {
        ...smtpSettings,
        subject
      }
    };

    setSaveStatus({ loading: true, message: t('msSavingSettings') });

    try {
      const response = await post('/personalize/mailactionsetting', mailSettings);
      if (response.data.success) {
        setSaveStatus({ success: true, message: t('msSettingsSavedSuccessfully') });
      } else {
        setSaveStatus({ success: false, message: t('msFailedToSaveSettings') });
      }
    } catch (error) {
      console.error(t('msErrorSavingMailSettings'), error);
      setSaveStatus({ success: false, message: t('msErrorSavingMailSettings') });
    }
  };

  const handleAddVariable = () => {
    if (selectedVariable) {
      setMailTemplate(prev => prev + selectedVariable);
    }
  };

  const smtpPane = (
    <Tab.Pane>
      <Form.Group widths='equal'>
        <Form.Input
          fluid
          label={t('msSmtpHost')}
          placeholder={t('msEnterSmtpHost')}
          name='host'
          value={smtpSettings.host}
          onChange={handleSmtpChange}
        />
        <Form.Input
          fluid
          label={t('msSmtpPort')}
          placeholder={t('msEnterSmtpPort')}
          name='port'
          value={smtpSettings.port}
          onChange={handleSmtpChange}
        />
      </Form.Group>
      <Form.Group widths='equal'>
        <Form.Input
          fluid
          label={t('msSmtpUser')}
          placeholder={t('msEnterSmtpUsername')}
          name='user'
          value={smtpSettings.user}
          onChange={handleSmtpChange}
        />
        <Form.Input
          fluid
          type='password'
          label={t('msSmtpPassword')}
          placeholder={t('msEnterSmtpPassword')}
          name='pass'
          value={smtpSettings.pass}
          onChange={handleSmtpChange}
        />
      </Form.Group>
      <Form.Group widths='equal'>
        <Form.Input
          fluid
          label={t('msFromEmail')}
          placeholder={t('msEnterFromEmail')}
          name='from'
          value={smtpSettings.from}
          onChange={handleSmtpChange}
        />
        <Form.Checkbox
          label={t('msSecureConnection')}
          name='secure'
          checked={smtpSettings.secure}
          onChange={(e, { name, checked }) => handleSmtpChange(e, { name, value: checked })}
        />
      </Form.Group>
    </Tab.Pane>
  );

  const sendinbluePane = (
    <Tab.Pane>
      <Form.Input
        fluid
        label={t('msSendinblueApiKey')}
        placeholder={t('msEnterSendinblueApiKey')}
        name='sendinblueApiKey'
        value={smtpSettings.sendinblueApiKey || ''}
        onChange={handleSmtpChange}
      />
    </Tab.Pane>
  );

  const panes = [
    { menuItem: 'SMTP', render: () => smtpPane },
    { menuItem: 'Sendinblue (Brevo)', render: () => sendinbluePane },
  ];

  return (
    <Segment>
      <Form>
        <Grid columns={2} divided>
          <Grid.Row>
            <Grid.Column>
              <Header as='h3'>{t('msMailTemplate')}</Header>
              <Form.Field
                control={Input}
                label={t('msSubject')}
                placeholder={t('msEnterMailSubject')}
                value={subject}
                onChange={handleSubjectChange}
              />
              <Form.Field>
                <label>{t('msMailContent')}</label>
                <ReactQuill 
                  value={mailTemplate} 
                  onChange={handleTemplateChange}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, false] }],
                      ['bold', 'italic', 'underline','strike', 'blockquote'],
                      [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
                      ['link', 'image'],
                      ['clean']
                    ],
                  }}
                />
              </Form.Field>
              <Form.Group>
                <Form.Field
                  control={Dropdown}
                  label={t('msAddVariable')}
                  placeholder={t('msSelectVariable')}
                  fluid
                  selection
                  options={variables}
                  value={selectedVariable}
                  onChange={(e, { value }) => setSelectedVariable(value)}
                />
                <Form.Field>
                  <Button 
                    onClick={handleAddVariable}
                    style={{ marginTop: '24px' }}
                  >
                    {t('msAdd')}
                  </Button>
                </Form.Field>
              </Form.Group>
            </Grid.Column>
            <Grid.Column>
              <Form.Field>
                <label>{t('msPreferredMailService')}</label>
                <Dropdown
                  selection
                  fluid
                  options={[
                    { key: 'smtp', text: 'SMTP', value: 'smtp' },
                    { key: 'sendinblue', text: 'Sendinblue (Brevo)', value: 'sendinblue' }
                  ]}
                  value={preferredMailService}
                  onChange={(e, { value }) => setPreferredMailService(value)}
                />
              </Form.Field>
  
              <Header as='h3'>{t('msEmailSettings')}</Header>
              <Tab panes={panes} onTabChange={handleTabChange} />
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />
        <Button primary onClick={handlePreview}>{t('msPreview')}</Button>
        <Button secondary onClick={handleSave} loading={saveStatus?.loading}>{t('msSaveSettings')}</Button>
        {saveStatus && (
          <Message
            positive={saveStatus.success}
            negative={!saveStatus.success}
            icon
          >
            <Icon name={saveStatus.success ? 'check' : 'warning sign'} />
            <Message.Content>
              <Message.Header>{saveStatus.success ? t('msSuccess') : t('msError')}</Message.Header>
              {saveStatus.message}
            </Message.Content>
          </Message>
        )}
      </Form>
      {preview && (
        <>
          <Divider />
          <Header as='h3'>{t('msPreview')}</Header>
          <Segment>
            <div dangerouslySetInnerHTML={{ __html: preview }} />
          </Segment>
        </>
      )}
    </Segment>
  );
};

const mapStateToProps = (state) => ({
  product: state.product.personalize
});

export default connect(mapStateToProps)(Mail);